
import {defineComponent} from "vue";
import {CodeDescriptionRestDto,} from "@/api/pharma-cpc-mgmt/models";
import {useToast} from "vue-toastification";
import {ErrorHandlerQueue} from "@/error/ErrorHandlerQueue";
import {DpExceptionsErrorHandler} from "@/error/handlers/DpExceptionsErrorHandler";
import {UIStateDto} from "@/dtos/UIStateDto";
import {AxiosErrorHandler} from "@/error/handlers/AxiosErrorHandler";
import AlertError2 from "@/components/UI/Bootstrap/Alert/AlertError2.vue";
import {EnumRestService} from "@/services/rest/enum/EnumRestService";
import {EnumClassNameCmsEnum} from "@/models/enum/EnumClassNameCmsEnum";
import BaseOffcanvasButtonClose from "@/components/UI/Bootstrap/Offcanvas/BaseOffcanvasButtonClose.vue";
import InputText from "@/components/UI/InputText.vue";
import {PageDetailModeEnum} from "@/models/enum/PageDetailModeEnum";
import {PopupTriggerTypeEnum} from "@/api/enums/popup-trigger-type-enum";
import {PropType} from "vue/dist/vue";
import {PopupTriggerRestService} from "@/services/rest/cms-popup/PopupTriggerRestService";
import {PopupCodeTriggerRestDtoModel} from "@/models/api/pharma-cms-popup/PopupCodeTriggerRestDtoModel";
import {PopupTimeDelayTriggerRestDtoModel} from "@/models/api/pharma-cms-popup/PopupTimeDelayTriggerRestDtoModel";
import InputNumber from "@/components/UI/InputNumber.vue";

export default defineComponent({
  name: "Trigger",
  emits: ['edit'],
  components: {
    InputNumber,
    InputText,
    BaseOffcanvasButtonClose,
    AlertError2
  },
  props: {
    popupId: {
      type: String,
      required: true,
    },
    id: {
      type: String,
      required: false,
    },
    triggerType: {
      type: String as PropType<PopupTriggerTypeEnum>,
    },
    mode: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      createTriggerType: null as PopupTriggerTypeEnum | null,
      triggerTypeOptions: [] as CodeDescriptionRestDto[],
      codeTrigger: PopupCodeTriggerRestDtoModel.createWithDefaults(),
      timeDelayTrigger: PopupTimeDelayTriggerRestDtoModel.createWithDefaults(),
      triggerChanged: false,
      toaster: useToast(),
      pageUI: UIStateDto.createWithDefaults(),
    }
  },
  mounted() {
    this.reloadContent();
  },
  computed: {
    PageDetailModeEnum() {
      return PageDetailModeEnum
    },
    PopupTriggerTypeEnum() {
      return PopupTriggerTypeEnum
    },
    theTriggerType(): PopupTriggerTypeEnum | null {
      if (this.mode === PageDetailModeEnum.NEW) {
        return this.createTriggerType;
      }

      return this.triggerType!;
    },
    triggerTypeDescription(): string {
      if (this.theTriggerType) {
        const triggerTypeCodeDescription = this.triggerTypeOptions.find(value => value.code === this.theTriggerType);
        if (triggerTypeCodeDescription) {
          return triggerTypeCodeDescription.description;
        }
      }

      return '';
    }
  },
  methods: {
    async submitData() {
      this.pageUI
        .clearError()
        .setNotReady();

      try {
        if (this.mode === PageDetailModeEnum.NEW) {
          if (this.createTriggerType === PopupTriggerTypeEnum.EXIT_INTENT_TRIGGER) {
            await PopupTriggerRestService.getInstance().addPopupExitIntentTrigger(this.popupId);
          }
          if (this.createTriggerType === PopupTriggerTypeEnum.CODE_TRIGGER) {
            await PopupTriggerRestService.getInstance()
              .addPopupCodeTrigger(this.codeTrigger, this.popupId);
          }
          if (this.createTriggerType === PopupTriggerTypeEnum.TIME_DELAY_TRIGGER) {
            await PopupTriggerRestService.getInstance()
              .addPopupTimeDelayTrigger(this.timeDelayTrigger, this.popupId);
          }
          this.$emit('edit');
          this.closeCurrentOffCanvas();
          return;
        }

        if (this.triggerType === PopupTriggerTypeEnum.CODE_TRIGGER) {
          await PopupTriggerRestService.getInstance()
            .updatePopupCodeTrigger(this.id, this.codeTrigger);
        }
        if (this.triggerType === PopupTriggerTypeEnum.TIME_DELAY_TRIGGER) {
          await PopupTriggerRestService.getInstance()
            .updatePopupTimeDelayTrigger(this.id, this.timeDelayTrigger);
        }
        this.$emit('edit');
        await this.afterSave();
      } catch (exceptions: any) {
        this.setExceptions(exceptions);
        window.scrollTo(0, 0);
      } finally {
        this.pageUI.setReady();
      }
    },
    async reloadContent() {
      this.pageUI
        .clearError()
        .setNotReady();
      try {
        this.triggerTypeOptions =
          await EnumRestService.getInstance().getCmsEnumValuesByClassname(EnumClassNameCmsEnum.POPUP_TRIGGER_TYPE);

        if (this.mode === PageDetailModeEnum.NEW) {
          this.createTriggerType = null;
        } else {
          if (this.triggerType === PopupTriggerTypeEnum.CODE_TRIGGER) {
            this.codeTrigger = await PopupTriggerRestService.getInstance().getPopupCodeTrigger(this.id);
          } else {
            this.timeDelayTrigger = await PopupTriggerRestService.getInstance().getPopupTimeDelayTrigger(this.id);
          }
        }
        this.triggerChanged = false;
      } catch (exceptions: any) {
        this.setExceptions(exceptions);
        window.scrollTo(0, 0);
      } finally {
        this.pageUI.setReady();
      }
    },
    async afterSave() {
      this.toaster.success(this.$t('savedSuccessfully'));
      await this.reloadContent();
    },
    async selectTriggerType(): Promise<void> {
      if (this.createTriggerType === PopupTriggerTypeEnum.EXIT_INTENT_TRIGGER) {
        return;
      }

      if (this.createTriggerType === PopupTriggerTypeEnum.CODE_TRIGGER) {
        this.codeTrigger = PopupCodeTriggerRestDtoModel.createWithDefaults();
        return;
      }

      this.timeDelayTrigger = PopupTimeDelayTriggerRestDtoModel.createWithDefaults();
    },
    onTriggerChanged(): void {
      this.triggerChanged = true;
    },
    setExceptions(exceptions: unknown): void {
      ErrorHandlerQueue
        .create()
        .add(DpExceptionsErrorHandler.createWithDefaultUIStateBehavior(this.pageUI as UIStateDto))
        .add(AxiosErrorHandler.createWithDefaultUIStateBehavior(this.pageUI as UIStateDto))
        .catch(exceptions, true);
    },
    closeCurrentOffCanvas(): void {
      // Wait one tick to ensure DOM is updated after the form is submitted
      this.$nextTick(() => {
        const closeButton = this.$refs.closeButton as any;
        if (closeButton && closeButton.$el) {
          closeButton.$el.click();
        }
      });
    }
  },
});
